"use client";

import { useState } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";

const KENYA_COUNTIES = [
  "Baringo", "Bomet", "Bungoma", "Busia", "Elgeyo-Marakwet",
  "Embu", "Garissa", "Homa Bay", "Isiolo", "Kajiado",
  "Kakamega", "Kericho", "Kiambu", "Kilifi", "Kirinyaga",
  "Kisii", "Kisumu", "Kitui", "Kwale", "Laikipia",
  "Lamu", "Machakos", "Makueni", "Mandera", "Marsabit",
  "Meru", "Migori", "Mombasa", "Murang\'a", "Nairobi",
  "Nakuru", "Nandi", "Narok", "Nyamira", "Nyandarua",
  "Nyeri", "Samburu", "Siaya", "Taita-Taveta", "Tana River",
  "Tharaka-Nithi", "Trans Nzoia", "Turkana", "Uasin Gishu",
  "Vihiga", "Wajir", "West Pokot",
];

const ORGANIZATION_TYPES = [
  "County Government",
  "Tourism Enterprise (Hotel/Lodge/Camp)",
  "Tour Operator / Travel Agency",
  "Community-Based Organization (CBO)",
  "Cultural / Arts Organization",
  "Investment Company",
  "Event Organization",
  "Religious Organization",
  "Other",
];

interface FormData {
  firstName: string;
  lastName: string;
  organizationName: string;
  organizationType: string;
  county: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  termsAccepted: boolean;
}

type Step = "details" | "verify" | "complete";

export default function SignUpFormClient() {
  const [step, setStep] = useState<Step>("details");
  const [otpSent, setOtpSent] = useState(false);
  const [otpValue, setOtpValue] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData & { otp: string }>>({});

  const [form, setForm] = useState<FormData>({
    firstName: "",
    lastName: "",
    organizationName: "",
    organizationType: "",
    county: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    termsAccepted: false,
  });

  const update = (field: keyof FormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<FormData & { otp: string }> = {};
    if (!form.firstName) newErrors.firstName = "First name is required";
    if (!form.lastName) newErrors.lastName = "Last name is required";
    if (!form.organizationName) newErrors.organizationName = "Organization name is required";
    if (!form.organizationType) newErrors.organizationType = "Please select an organization type";
    if (!form.county) newErrors.county = "Please select your county";
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = "Valid email required";
    if (!form.phone || form.phone.length < 10) newErrors.phone = "Valid phone number required";
    if (!form.password || form.password.length < 8) newErrors.password = "Password must be at least 8 characters";
    if (form.password !== form.confirmPassword) newErrors.confirmPassword = "Passwords do not match";
    if (!form.termsAccepted) newErrors.termsAccepted = false;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitDetails = () => {
    if (!validate()) return;
    setStep("verify");
    setTimeout(() => setOtpSent(true), 1000);
  };

  const handleVerify = () => {
    if (otpValue.length !== 6) {
      setErrors((e) => ({ ...e, otp: "Enter the 6-digit code" }));
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setEmailVerified(true);
      setStep("complete");
    }, 1500);
  };

  const labelClass = "block text-[0.6875rem] font-bold uppercase tracking-[0.3em] text-text-muted font-condensed";
  const inputClass = (field: keyof FormData) =>
    `w-full bg-warm-bg border-2 rounded-xl px-4 py-3.5 text-[0.9375rem] text-kenya-black placeholder:text-text-muted/45 form-input transition-all ${
      errors[field] ? "border-kenya-red/50 bg-kenya-red/[0.02]" : "border-kenya-black/12 focus:border-kenya-green"
    }`;

  if (step === "complete") {
    return (
      <div className="text-center py-12 space-y-6">
        <div className="w-20 h-20 rounded-full bg-kenya-green/12 flex items-center justify-center mx-auto">
          <Icon name="CheckBadgeIcon" size={42} className="text-kenya-green" />
        </div>
        <div>
          <h2 className="text-3xl font-display font-bold text-kenya-black mb-2">
            Account Created Successfully!
          </h2>
          <p className="text-text-muted text-[0.9375rem]">
            Welcome to the County Product Portal, <strong className="text-kenya-black">{form.firstName}</strong>.
          </p>
        </div>
        <div className="bg-kenya-green/8 border-2 border-kenya-green/20 rounded-xl p-6 text-left space-y-3">
          <div className="flex items-center gap-2">
            <Icon name="EnvelopeIcon" size={17} className="text-kenya-green" />
            <span className="text-[0.9375rem] font-medium text-kenya-black">Confirmation sent to {form.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="IdentificationIcon" size={17} className="text-kenya-green" />
            <span className="text-[0.9375rem] font-medium text-kenya-black">Account ID: KTP-{Date.now().toString().slice(-6)}</span>
          </div>
        </div>
        <Link
          href="/product-submission"
          className="inline-flex items-center gap-2 bg-kenya-red text-white px-8 py-4 rounded-xl font-bold text-[0.9375rem] tracking-[0.05em] hover:bg-kenya-red/90 transition-all shadow-kenya"
        >
          Submit Your First Product
          <Icon name="ArrowRightIcon" size={17} />
        </Link>
      </div>
    );
  }

  if (step === "verify") {
    return (
      <div className="space-y-8">
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-full bg-kenya-red/10 flex items-center justify-center mx-auto">
            <Icon name="DevicePhoneMobileIcon" size={30} className="text-kenya-red" />
          </div>
          <h3 className="text-2xl font-bold text-kenya-black font-display">Verify Your Contact</h3>
          <p className="text-text-muted text-[0.9375rem]">
            We&apos;ve sent a 6-digit code to{" "}
            <strong className="text-kenya-black">{form.phone}</strong>
          </p>
        </div>

        {/* OTP Input */}
        <div className="space-y-2">
          <label className={labelClass}>
            Verification Code
          </label>
          <input
            type="text"
            maxLength={6}
            value={otpValue}
            onChange={(e) => setOtpValue(e.target.value.replace(/\D/g, ""))}
            placeholder="000000"
            className={`w-full bg-warm-bg border-2 rounded-xl px-4 py-4 text-2xl font-mono text-center tracking-[0.5em] text-kenya-black form-input transition-all ${
              errors.otp ? "border-kenya-red/50" : "border-kenya-black/12"
            }`}
          />
          {errors.otp && <p className="text-[0.875rem] text-kenya-red font-medium">{errors.otp}</p>}
          <p className="text-[0.8125rem] text-text-muted text-center">
            Demo: use any 6 digits
          </p>
        </div>

        {/* Email verification notice */}
        <div className="bg-kenya-black/4 border-2 border-kenya-black/10 rounded-xl p-4 flex items-center gap-3">
          <Icon name="EnvelopeIcon" size={19} className="text-kenya-black/45 flex-shrink-0" />
          <div className="text-[0.9375rem] text-text-muted">
            Email verification link also sent to{" "}
            <strong className="text-kenya-black">{form.email}</strong>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setStep("details")}
            className="flex-1 py-4 border-2 border-kenya-black/12 rounded-xl text-[0.9375rem] font-bold text-text-muted hover:border-kenya-black/25 hover:text-kenya-black transition-all"
          >
            Back
          </button>
          <button
            onClick={handleVerify}
            disabled={loading}
            className="flex-1 py-4 bg-kenya-red text-white rounded-xl text-[0.9375rem] font-bold tracking-[0.05em] hover:bg-kenya-red/90 transition-all shadow-kenya disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify & Create Account"
            )}
          </button>
        </div>

        <p className="text-center text-[0.9375rem] text-text-muted">
          Didn&apos;t receive the code?{" "}
          <button className="text-kenya-red font-bold hover:underline">Resend</button>
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmitDetails();
      }}
      className="space-y-6"
    >
      {/* Name row */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className={labelClass}>
            First Name <span className="text-kenya-red">*</span>
          </label>
          <input
            type="text"
            value={form.firstName}
            onChange={(e) => update("firstName", e.target.value)}
            placeholder="Amara"
            className={inputClass("firstName")}
          />
          {errors.firstName && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.firstName}</p>}
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            Last Name <span className="text-kenya-red">*</span>
          </label>
          <input
            type="text"
            value={form.lastName}
            onChange={(e) => update("lastName", e.target.value)}
            placeholder="Wanjiku"
            className={inputClass("lastName")}
          />
          {errors.lastName && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.lastName}</p>}
        </div>
      </div>

      {/* Organization */}
      <div className="space-y-2">
        <label className={labelClass}>
          Organization Name <span className="text-kenya-red">*</span>
        </label>
        <input
          type="text"
          value={form.organizationName}
          onChange={(e) => update("organizationName", e.target.value)}
          placeholder="e.g. Narok County Tourism Office"
          className={inputClass("organizationName")}
        />
        {errors.organizationName && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.organizationName}</p>}
      </div>

      {/* Organization type + County */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className={labelClass}>
            Organization Type <span className="text-kenya-red">*</span>
          </label>
          <select
            value={form.organizationType}
            onChange={(e) => update("organizationType", e.target.value)}
            className={`${inputClass("organizationType")} appearance-none cursor-pointer`}
          >
            <option value="">Select type...</option>
            {ORGANIZATION_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          {errors.organizationType && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.organizationType}</p>}
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            County <span className="text-kenya-red">*</span>
          </label>
          <select
            value={form.county}
            onChange={(e) => update("county", e.target.value)}
            className={`${inputClass("county")} appearance-none cursor-pointer`}
          >
            <option value="">Select county...</option>
            {KENYA_COUNTIES.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {errors.county && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.county}</p>}
        </div>
      </div>

      {/* Email */}
      <div className="space-y-2">
        <label className={labelClass}>
          Email Address <span className="text-kenya-red">*</span>
        </label>
        <input
          type="email"
          value={form.email}
          onChange={(e) => update("email", e.target.value)}
          placeholder="amara.wanjiku@naroktourism.go.ke"
          className={inputClass("email")}
        />
        {errors.email && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.email}</p>}
      </div>

      {/* Phone */}
      <div className="space-y-2">
        <label className={labelClass}>
          Phone Number <span className="text-kenya-red">*</span>
        </label>
        <div className="flex gap-2">
          <div className="flex items-center gap-2 px-3 bg-warm-bg border-2 border-kenya-black/12 rounded-xl text-[0.9375rem] text-text-muted font-medium min-w-[88px]">
            &#127472;&#127466; +254
          </div>
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            placeholder="712 345 678"
            className={`flex-1 bg-warm-bg border-2 rounded-xl px-4 py-3.5 text-[0.9375rem] text-kenya-black placeholder:text-text-muted/45 form-input transition-all ${
              errors.phone ? "border-kenya-red/50" : "border-kenya-black/12"
            }`}
          />
        </div>
        {errors.phone && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.phone}</p>}
      </div>

      {/* Password */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className={labelClass}>
            Password <span className="text-kenya-red">*</span>
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              placeholder="Min. 8 characters"
              className={`${inputClass("password")} pr-10`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-kenya-black transition-colors"
            >
              <Icon name={showPassword ? "EyeSlashIcon" : "EyeIcon"} size={17} />
            </button>
          </div>
          {errors.password && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.password}</p>}
        </div>
        <div className="space-y-2">
          <label className={labelClass}>
            Confirm Password <span className="text-kenya-red">*</span>
          </label>
          <input
            type="password"
            value={form.confirmPassword}
            onChange={(e) => update("confirmPassword", e.target.value)}
            placeholder="Repeat password"
            className={inputClass("confirmPassword")}
          />
          {errors.confirmPassword && <p className="text-[0.8125rem] text-kenya-red font-medium">{errors.confirmPassword}</p>}
        </div>
      </div>

      {/* Password strength */}
      {form.password && (
        <div className="space-y-1.5">
          <div className="flex gap-1">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  form.password.length >= i * 3
                    ? i <= 2
                      ? "bg-kenya-red" :"bg-kenya-green" :"bg-kenya-black/10"
                }`}
              />
            ))}
          </div>
          <p className="text-[0.8125rem] text-text-muted font-medium">
            {form.password.length < 6
              ? "Weak password"
              : form.password.length < 10
              ? "Fair password" :"Strong password"}
          </p>
        </div>
      )}

      {/* Terms */}
      <label className="flex items-start gap-3 cursor-pointer group">
        <div className="relative mt-0.5">
          <input
            type="checkbox"
            checked={form.termsAccepted}
            onChange={(e) => update("termsAccepted", e.target.checked)}
            className="sr-only"
          />
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
              form.termsAccepted
                ? "bg-kenya-green border-kenya-green"
                : errors.termsAccepted === false
                ? "border-kenya-red" :"border-kenya-black/25 group-hover:border-kenya-green/50"
            }`}
          >
            {form.termsAccepted && <Icon name="CheckIcon" size={12} className="text-white" />}
          </div>
        </div>
        <span className="text-[0.9375rem] text-text-muted leading-relaxed">
          I agree to the{" "}
          <a href="#" className="text-kenya-red font-semibold hover:underline">
            Terms & Conditions
          </a>{" "}
          and{" "}
          <a href="#" className="text-kenya-red font-semibold hover:underline">
            Privacy Policy
          </a>{" "}
          of the Ministry of Tourism & Wildlife, Kenya.
        </span>
      </label>
      {errors.termsAccepted === false && (
        <p className="text-[0.8125rem] text-kenya-red font-medium -mt-4">You must accept the terms to continue</p>
      )}

      {/* Submit */}
      <button
        type="submit"
        className="w-full py-4 bg-kenya-red text-white rounded-xl font-bold text-[0.9375rem] tracking-[0.05em] hover:bg-kenya-red/90 transition-all shadow-kenya flex items-center justify-center gap-2"
      >
        Continue to Verification
        <Icon name="ArrowRightIcon" size={17} />
      </button>

      <p className="text-center text-[0.9375rem] text-text-muted">
        Already have an account?{" "}
        <Link href="/homepage" className="text-kenya-red font-bold hover:underline">
          Sign In
        </Link>
      </p>
    </form>
  );
}